// Core packages
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom packages - Models
import { AuthService } from "src/app/modules/auth/auth.service";
import { RolesService } from "src/app/modules/roles/roles.service";
import { UsersService } from "src/app/modules/users/users.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ThemeService } from "src/app/shared/services/theme.service";

@Component({
  selector: "app-add-user",
  templateUrl: "./add-user.page.html",
  styleUrls: ["./add-user.page.scss"]
})
export class AddUserComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  availableRoles = [];
  loggedUserRequestedRoleId: string;
  loggedUserRequestedRoleType: string;
  loggedUsersCurrentCompanyId = "";

  // BEGIN - Add user form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  previousUserId = "";
  currentUserEmail = "";
  userAlreadyExists = false;
  // END - Add user form

  /**
   * Class constructor
   */
  constructor(
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    public helperService: HelperService,
    public configService: ConfigService,
    private themeService: ThemeService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private usersService: UsersService,
    private router: Router,
    private toastrService: ToastrService,
    private rolesService: RolesService,
    private apiService: ApiService // DO NOT MOVE since it's used for 'autocomplete' field type
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Load correct data into component basing on url params
    if (this.activatedRoute.params) {
      this.subscriptions.push(
        this.activatedRoute.params.subscribe(
          res => {
            if (res) {
              if (res.lang) {
                this.lang = res.lang;
              }
              this.handleRouteParamsChange();
            }
          },
          err => {
            // if we came here users with given userId do not exist
            this.router.navigate([this.lang, "errors", "404"]);
          }
        )
      );
    }
  }

  /**
   * After view init logic
   *
   */
  ngAfterViewInit(): void {
    //   this.updateValues();
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  handleRouteParamsChange(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("users.add.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserRequestedRole$.subscribe(
        res => (this.loggedUserRequestedRoleId = res)
      )
    );
    this.subscriptions.push(
      this.authService.loggedUserRequestedRoleType$.subscribe(
        res => (this.loggedUserRequestedRoleType = res)
      )
    );

    // Get roles data
    if (
      this.loggedUserRequestedRoleType === "admin" ||
      this.loggedUserRequestedRoleType === "operator"
    ) {
      this.loggedUsersCurrentCompanyId = "";
      this.availableRoles = this.rolesService.roleTypes.filter((obj: any) => {
        return obj.value === "admin" || obj.value === "operator" || obj.value === "tagger";
      });
    }
    if (
      this.loggedUserRequestedRoleType === "master" ||
      this.loggedUserRequestedRoleType === "collaborator"
    ) {
      this.availableRoles = this.rolesService.roleTypes.filter((obj: any) => {
        return obj.value === "master" || obj.value === "collaborator";
      });

      const index = this.loggedUser.userRoles.findIndex((obj: any) => {
        return obj._id === this.loggedUserRequestedRoleId;
      });
      this.loggedUsersCurrentCompanyId = this.loggedUser.userRoles[index].company._id;
    }

    // Prevent form fields to be rendered twice on url params change
    this.fields = [];

    // Init add form
    this.initForm();
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = new User();

    // Set fields
    this.fields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 col-sm-6",
            type: "select",
            key: "role",
            templateOptions: {
              label: this.translateService.instant("users.fields.role"),
              placeholder: this.translateService.instant("users.fields.role"),
              required: true,
              options: this.availableRoles,
              typeToSearchText: this.translateService.instant(
                "common.words.typeToSearch"
              ),
              clearAllText: this.translateService.instant(
                "common.words.clearAll"
              ),
              notFoundText: this.translateService.instant(
                "common.words.notFound"
              ),
              loadingText: this.translateService.instant("common.words.loading")
            }
          },
          {
            className: "col-12 col-sm-6",
            type: "autocomplete",
            key: "email",
            templateOptions: {
              customLabel: this.translateService.instant(
                "users.fields.email"
              ),
              placeholder: this.translateService.instant(
                "users.fields.email"
              ),
              required: true,
              bindLabel: "email",
              bindValue: "_id",
              multiple: false,
              search$: this.usersService.getList.bind(this),
              getAll: true,
              hideSelected: true,
              addTag: (term: string) => {
                const regex = new RegExp(this.configService.emailPattern);
                if (regex.test(term)) {
                  return term;
                } else {
                  this.toastrService.warning(this.translateService.instant("common.errors.invalid_email_address"));
                  return false;
                }
              },
              addTagText: this.translateService.instant(
                "common.words.addTag"
              ),
              // typeToSearchText: this.translateService.instant(
              //   "common.words.typeToSearch", { minTermLength: 2 }
              // ),
              clearAllText: this.translateService.instant(
                "common.words.clearAll"
              ),
              notFoundText: this.translateService.instant(
                "common.words.notFound"
              ),
              loadingText: this.translateService.instant("common.words.loading")
            },
            expressionProperties: {
              'templateOptions.onChange': (value: any) => {
                this.updateValues(value.email);
              }
            },
          },
          {
            className: "col-12 col-sm-6",
            type: "input",
            key: "firstName",
            templateOptions: {
              label: this.translateService.instant("users.fields.firstName"),
              placeholder: this.translateService.instant(
                "users.fields.firstName"
              ),
              required: true,
              minLength: 2,
              maxLength: 50
            }
          },
          {
            className: "col-12 col-sm-6",
            type: "input",
            key: "lastName",
            templateOptions: {
              label: this.translateService.instant("users.fields.lastName"),
              placeholder: this.translateService.instant(
                "users.fields.lastName"
              ),
              required: true,
              minLength: 2,
              maxLength: 50
            }
          },
          // {
          //   className: "col-12 col-sm-6",
          //   type: "input",
          //   key: "taxCode",
          //   templateOptions: {
          //     label: this.translateService.instant("users.fields.taxCode"),
          //     placeholder: this.translateService.instant(
          //       "users.fields.taxCode"
          //     ),
          //     required: false,
          //     minLength: 14,
          //     maxLength: 17,
          //     // addonLeft: {
          //     //   class: "far fa-address-card"
          //     // }
          //   }
          // },
          // {
          //   className: "col-12 col-sm-6",
          //   type: "input",
          //   key: "phone",
          //   templateOptions: {
          //     label: this.translateService.instant("users.fields.phone"),
          //     placeholder: this.translateService.instant("users.fields.phone"),
          //     required: false,
          //     minLength: 1,
          //     maxLength: 15,
          //     pattern: this.configService.phonePattern,
          //     // addonLeft: {
          //     //   class: "far fa-phone"
          //     // }
          //   }
          // },
          // {
          //   className: "col-12 col-sm-6",
          //   type: "address",
          //   key: "address",
          //   templateOptions: {
          //     label: this.translateService.instant("users.fields.address"),
          //     placeholder: this.translateService.instant(
          //       "users.fields.address"
          //     ),
          //     required: false,
          //     // addonLeft: {
          //     //   class: "far fa-map-marker-alt"
          //     // }
          //   }
          // }
        ]
      }
    ];
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    // set value and get value disabled
    const model = {
      //address: this.form.value.address ? this.form.value.address : this.form.controls.address.value,
      email: this.form.value.email ? this.form.value.email : this.form.controls.email.value,
      firstName: this.form.value.firstName ? this.form.value.firstName : this.form.controls.firstName.value,
      lastName: this.form.value.lastName ? this.form.value.lastName : this.form.controls.lastName.value,
      //phone: this.form.value.phone ? this.form.value.phone : this.form.controls.phone.value,
      role: this.form.value.role ? this.form.value.role : this.form.controls.role.value,
      //taxCode: this.form.value.taxCode ? this.form.value.taxCode : this.form.controls.taxCode.value,
    };
    // model.email: check value like _id and change it with user email
    const regex = new RegExp(this.configService.emailPattern);
    if (this.previousUserId && !regex.test(this.previousUserId)) {
      model.email = this.currentUserEmail;
    }

    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      console.warn(this.form);
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    // Remove blank values from model
    Object.keys(model).forEach((key: string) => {
      if (typeof model[key] === 'string' && model[key].trim() === "") {
        model[key] = undefined;
      }
    });

    // Get corresponding role id based on type
    this.subscriptions.push(
      this.rolesService
        .getList(0, 1, "", "", {
          type: this.form.controls["role"].value
        })
        .subscribe(
          (res: any) => {
            if (res) {
              if (res.totalCount === 0) {
                this.toastrService.error("Nessun ruolo corrispondente trovato");
                this.formBtnOptions.active = false;
                return;
              }

              if (res.status) {
                const selectedRole = res.data.items[0];
                model["role"] = selectedRole._id;

                // Submit data
                this.subscriptions.push(
                  this.usersService
                    .create(model)
                    .pipe(finalize(() => (this.formBtnOptions.active = false)))
                    .subscribe(
                      resUser => {
                        if (resUser.status) {
                          this.toastrService.success(resUser.message);
                          setTimeout(() => {
                            const lang = this.translateService.currentLang;
                            this.router.navigate([
                              lang,
                              "app",
                              "users",
                              "detail",
                              resUser.data._id
                            ]);
                          }, this.configService.redirectDelayAfterCreation);
                          return;
                        }
                      },
                      errUser => {
                        this.helperService.handleError(errUser);
                      }
                    )
                );
              }
            }
          },
          err => {
            this.helperService.handleError(err);
          }
        )
    );
  }

  /**
   * Update values based on userId changes
   */
  updateValues(value: any): void {
    if (this.previousUserId !== value) {
      this.previousUserId = value;
      const regex = new RegExp(this.configService.emailPattern);
      const roleControl = this.form.get("role");
      const firstNameControl = this.form.get("firstName");
      const lastNameControl = this.form.get("lastName");
      //const taxCodeControl = this.form.get("taxCode");
      //const phoneControl = this.form.get("phone");
      //const addressControl = this.form.get("address");
      if (roleControl.value === "") {
        roleControl.setValue("");
      }
      roleControl.enable();
      firstNameControl.setValue("");
      firstNameControl.enable();
      lastNameControl.setValue("");
      lastNameControl.enable();
      // taxCodeControl.setValue("");
      // taxCodeControl.enable();
      // phoneControl.setValue("");
      // phoneControl.enable();
      // addressControl.setValue("");
      // addressControl.enable();

      if (this.previousUserId && !regex.test(this.previousUserId)) {
        this.usersService.get(this.previousUserId).subscribe(
          res => {
            if (res.status) {
              this.currentUserEmail = res.data.email;
              if (res.data.firstName) {
                firstNameControl.setValue(res.data.firstName);
                firstNameControl.disable();
              }
              if (res.data.lastName) {
                lastNameControl.setValue(res.data.lastName);
                lastNameControl.disable();
              }
              // if (res.data.taxCode) {
              //   taxCodeControl.setValue(res.data.taxCode);
              //   taxCodeControl.disable();
              // }
              // if (res.data.phone) {
              //   phoneControl.setValue(res.data.phone);
              //   phoneControl.disable();
              // }
              // if (res.data.address) {

              //   const address = {
              //     route: res.data.address.route ? res.data.address.route : "",
              //     streetNumber: res.data.address.streetNumber ? res.data.address.streetNumber : "",
              //     city: res.data.address.postalCode ? res.data.address.postalCode : "",
              //     postalCode: res.data.address.city ? res.data.address.city : "",
              //     countryCodeTwo: res.data.address.countryCodeTwo ? res.data.address.countryCodeTwo : "",
              //     latLng: res.data.address.latLng ? res.data.address.latLng : "",
              //     administrativeArea3: res.data.address.administrativeArea3 ? res.data.address.administrativeArea3 : "",
              //     administrativeArea2: res.data.address.administrativeArea2 ? res.data.address.administrativeArea2 : "",
              //     administrativeArea1: res.data.address.administrativeArea1 ? res.data.address.administrativeArea1 : "",
              //   };
              //   addressControl.setValue(address);
              //   addressControl.disable();
              // }

              // current login Archedi: check user exists in Archedi
              if (
                this.loggedUserRequestedRoleType === "admin" ||
                this.loggedUserRequestedRoleType === "operator"
              ) {
                if (res.data.userRoles
                  && Array.isArray(res.data.userRoles)
                  && res.data.userRoles.length) {
                  const index = res.data.userRoles.findIndex((obj: any) => {
                    return obj.role.type === "admin" || obj.role.type === "operator";
                  });
                  if (index > -1) {
                    // user already exists
                    this.userAlreadyExists = true;
                    roleControl.setValue(res.data.userRoles[index].role.type);
                    roleControl.disable();
                    this.toastrService.warning(this.translateService.instant("users.userExists"));
                  } else {
                    this.userAlreadyExists = false;
                  }
                }
              } else { // current login Company: check user exists in current company
                if (res.data.userRoles
                  && Array.isArray(res.data.userRoles)
                  && res.data.userRoles.length) {
                  const index = res.data.userRoles.findIndex((obj: any) => {
                    return obj.company && obj.company._id === this.loggedUsersCurrentCompanyId;
                  });
                  if (index > -1) {
                    // user already exists
                    this.userAlreadyExists = true;
                    roleControl.setValue(res.data.userRoles[index].role.type);
                    roleControl.disable();
                    this.toastrService.warning(this.translateService.instant("users.userExists"));
                  } else {
                    this.userAlreadyExists = false;
                  }
                }
              }
            }
          },
          err => {
            this.helperService.handleError(err);
          }
        );
      } else {
        this.currentUserEmail = "";
        this.userAlreadyExists = false;
      }
    }
  }
}
